import { Utils } from './Utils';
import { Store, Withdrawal } from '../models/RestModels';

/**
 * Display-ready withdrawal receipt data.
 *
 * Mirrors the values shown by PrintWithdrawalComponent
 * (src/app/pages/print-withdrawal). The caller owns every value; this
 * builder only formats those same values as ESC/POS XML so the USB receipt
 * prints the same content as the browser receipt.
 */
export interface WithdrawalTicketData {
	withdrawal: Withdrawal;
	store: Store;
	user_name: string;
}

export class WithdrawalTicketBuilder {

	private data: WithdrawalTicketData;

	ticket_a_columns: number = 42;
	ticket_b_columns: number = 50;
	font_row: string = 'b';

	constructor(data: WithdrawalTicketData, ticket_a_columns: number = 42, ticket_b_columns: number = 50) {
		this.data = data;
		this.ticket_a_columns = ticket_a_columns;
		this.ticket_b_columns = ticket_b_columns;
	}

	private escapeXml(str: string): string {
		if (!str) return '';
		return str
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&apos;');
	}

	// Same output as the Angular titlecase pipe: first letter of each word
	// upper case, rest of the word lower case.
	private formatTitleCase(value: string | null | undefined): string {
		if (!value) return '';
		return value.replace(/\S+/g, (word) => word[0].toUpperCase() + word.substr(1).toLowerCase());
	}

	private formatShortDate(value: any): string {
		return Utils.getDateString(value, true);
	}

	private buildSectionTitle(title: string): string {
		return `<Text justification="center" bold="true">${this.escapeXml(title)}</Text>\n<Feed lines="1"></Feed>\n`;
	}

	public buildXml(): string {
		let xml = '<EscPos>\n';
		xml += '<Init></Init>\n';
		xml += this.buildHeader();
		xml += this.buildBody();
		xml += this.buildSignature();
		xml += '<Feed lines="2"></Feed>\n';
		xml += '<Cut mode="partial"></Cut>\n';
		xml += '</EscPos>';
		return xml;
	}

	private buildHeader(): string {
		let xml = '';
		const store = this.data.store;

		if (store?.business_name) {
			xml += `<Text justification="center" bold="true">${this.escapeXml(store.business_name)}</Text>\n`;
		}
		if (store?.rfc) {
			xml += `<Text justification="center">${this.escapeXml(store.rfc)}</Text>\n`;
		}
		const parts = [store?.name, store?.address]
			.filter(p => !!p)
			.map(p => this.escapeXml(p as string));
		if (store?.city) {
			parts.push(this.escapeXml(this.formatTitleCase(store.city)));
		}
		if (store?.phone) {
			parts.push(this.escapeXml(store.phone));
		}
		if (parts.length) {
			xml += `<Text justification="center">${parts.join(' ')}</Text>\n`;
		}
		xml += '<Feed lines="1"></Feed>\n';

		return xml;
	}

	private buildBody(): string {
		let xml = '';
		const withdrawal = this.data.withdrawal;
		const amount = withdrawal?.amount ?? '';

		xml += this.buildSectionTitle(`Recibo de Retiro Folio #${withdrawal?.id ?? ''}`);
		xml += `<Text font="b" justification="left">Fecha: </Text><Text font="b" bold="true" justification="left">#${this.escapeXml(this.formatShortDate(withdrawal?.created))}</Text><Feed lines="1"></Feed>\n`;
		xml += `<Text font="b" justification="left">Atendio: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(this.data.user_name || '')}</Text><Feed lines="1"></Feed>\n`;
		xml += `<Text font="b" justification="left">Monto Retirado: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(`${amount} ${withdrawal?.currency_id || ''}`.trim())}</Text><Feed lines="1"></Feed>\n`;
		if (withdrawal?.note) {
			xml += `<Text font="b" justification="left">Concepto: </Text><Text font="b" bold="true" justification="left">${this.escapeXml(withdrawal.note)}</Text><Feed lines="1"></Feed>\n`;
		}
		xml += '<Feed lines="1"></Feed>\n';
		xml += `<Text justification="left">El retiro por la cantidad de: </Text><Text bold="true" justification="left">$${this.escapeXml(`${amount}`)}</Text><Feed lines="1"></Feed>\n`;
		xml += `<Text justification="left">Solo es valido cuando presenta la firma del Gerente</Text><Feed lines="1"></Feed>\n`;

		return xml;
	}

	private buildSignature(): string {
		let xml = '<Feed lines="1"></Feed>\n';
		xml += `<Text justification="left" bold="true">Recibio:</Text><Feed lines="1"></Feed>\n`;
		xml += '<Feed lines="3"></Feed>\n';
		return xml;
	}
}
